import { AxiosError } from "axios";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { getEmployeesOverview } from "../api/Esop";
import {
  approveExercise,
  downloadHistoricExerciseTemplate,
  getAllExerciseRequests,
  getExercisableOptionsDetails,
  getExerciseRequestDetails,
  historicalExercise,
  importHistoricalExercise,
  rejectExerciseRequest,
  requestToExercise,
  uploadExerciseLetters,
  validateHistoricalExercise,
} from "../api/exercise";
import { Result } from "../types/Exercise";
import { queryClient } from "./client";

export function useExercisableOptionsDetails(
  optionHolderId: string,
  date: string
) {
  return useQuery({
    queryKey: ["exercisableOptions"],
    queryFn: async () => {
      const response = await getExercisableOptionsDetails(optionHolderId, date);
      return response.data;
    },
    enabled: false,
  });
}

export function useRequestToExercise() {
  return useMutation({
    mutationKey: "doExerciseRequest",
    mutationFn: requestToExercise,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("employeeVestingTableDetails");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("employeeVestingTableDetails");
    },
    onError: () => {},
  });
}

export function useExerciseRequestDetails(optionHolderId: string) {
  return useQuery({
    queryKey: ["exerciseRequestDetails"],
    queryFn: async () => (await getExerciseRequestDetails(optionHolderId)).data,
    staleTime: 0,
    retry: false,
    onError: (error: AxiosError<Result>) =>
      toast(`Cannot Approve Exercise Because ${error.response?.data.reason}`, {
        type: "error",
        autoClose: 2000,
      }),
  });
}

export function useApproveExercise() {
  return useMutation({
    mutationKey: "approveExercise",
    mutationFn: approveExercise,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("employeeVestingTableDetails");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("getAllExerciseRequests");
      queryClient.invalidateQueries("overview");
      queryClient.refetchQueries("overview");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("employeeVestingTableDetails");
      queryClient.refetchQueries("getAllExerciseRequests");
    },
    onError: () => {},
  });
}

export function useRejectExerciseRequest() {
  return useMutation({
    mutationKey: "rejectExerciseRequest",
    mutationFn: rejectExerciseRequest,
    onSuccess: () => {
      queryClient.invalidateQueries("getAllExerciseRequests");
      queryClient.refetchQueries("getAllExerciseRequests");
    },
  });
}

export function useGetAllExerciseRequests() {
  return useQuery({
    queryKey: ["getAllExerciseRequests"],
    queryFn: getAllExerciseRequests,
  });
}

export function useDownloadHistoricExerciseTemplate() {
  return useQuery({
    queryKey: "downloadHistoricExerciseTemplate",
    queryFn: downloadHistoricExerciseTemplate,
    enabled: false,
  });
}

export function useHistoricalExercise() {
  return useMutation({
    mutationKey: "historicalExercise",
    mutationFn: historicalExercise,
  });
}

export function useValidateHistoricalExercise() {
  return useMutation({
    mutationKey: "validateHistoricalExercise",
    mutationFn: validateHistoricalExercise,
  });
}

export function useImportHistoricalExercise() {
  return useMutation({
    mutationKey: "importHistoricalExercise",
    mutationFn: importHistoricalExercise,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("employeeVestingTableDetails");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("overview");
    },
  });
}

export function useUploadExerciseLetters() {
  return useMutation({
    mutationKey: "uploadExerciseLetters",
    mutationFn: uploadExerciseLetters,
  });
}

// Fetches the company employee list and returns a Map<hrId, employeeId (DB UUID)>.
// Fired only when the letters upload step is active.
export function useExerciseEmployeeMap(enabled: boolean) {
  return useQuery({
    queryKey: "exerciseEmployeeMap",
    queryFn: async () => {
      const res = await getEmployeesOverview();
      const map = new Map<string, string>();
      res.data.employees.forEach((emp) => {
        if (emp.employeeIdentificationString && emp.id) {
          map.set(emp.employeeIdentificationString, emp.id);
        }
      });
      return map;
    },
    enabled,
  });
}
